import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { appConfirmMessage } from '../../../utils/appConfirm';
import { toast } from 'react-toastify';
import {
  FiPlus,
  FiUploadCloud,
  FiSearch,
  FiEdit2,
  FiTrash2,
  FiExternalLink,
  FiEye,
  FiAlertTriangle,
  FiCalendar,
  FiShield,
  FiCheckCircle,
  FiUser,
  FiBriefcase,
  FiAward,
  FiBookOpen,
  FiHeart,
  FiLock,
  FiLayers,
  FiFileText,
} from 'react-icons/fi';
import Cookies from 'js-cookie';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import { useAdminPageSearch } from '../../../utils/useAdminPageSearch';
import '../styles/Document.css';
import AddDocument from './AddDocument';
import EditDocument from './EditDocument';
import BulkUpload from './BulkUpload';
import LicencesCompliance from './LicencesCompliance';
import {
  DOCUMENT_CATEGORY_TYPES,
  DOCUMENT_TYPE_OPTIONS,
  categorizeDocumentType,
} from '../constants/documentTypes';
import {
  fetchAllCompanyUsers,
  isWorkforcePerson,
  workforceRoleLabel,
} from '../utils/workforcePeople';

const GET_WORKFORCE_DOCUMENTS = `
  query GetWorkforceDocuments {
    getWorkforceDocuments {
      id
      userId
      employeeCode
      employeeName
      cleaningLicense
      experienceYears
      documentType
      fileName
      fileKey
      fileUrl
      fileType
      verificationStatus
      verifiedBy
      expiryDate
      siteReference
      createdAt
    }
  }
`;

const UPDATE_DOC_VERIFICATION = `
  mutation UpdateWorkforceDocumentVerification($input: UpdateWorkforceDocumentInput!) {
    updateWorkforceDocument(input: $input) {
      id
      verificationStatus
      verifiedBy
    }
  }
`;

const REMOVE_WORKFORCE_DOCUMENT = `
  mutation RemoveWorkforceDocument($id: String!) {
    removeWorkforceDocument(id: $id)
  }
`;

const GET_ALL_USERS = `
  query GetAllUser($page: Int, $limit: Int) {
    getAllUser(page: $page, limit: $limit) {
      data {
        id
        firstName
        lastName
        roles
        role
        employeeCode
        branchId
      }
      total
      totalPages
      page
    }
  }
`;

const GET_BRANCHES = `
  query DocumentBranches {
    getMyCompanyBranches {
      id
      name
      location
    }
  }
`;

const SEND_EXPIRY_EMAILS = `
  mutation SendWorkforceDocumentExpiryEmails($thresholds: [Int!]) {
    sendWorkforceDocumentExpiryEmails(thresholds: $thresholds) {
      sent
      itemCount
      status
      message
    }
  }
`;

const ALERT_TOGGLES_KEY = 'workforce-doc-expiry-alerts:v1';

const loadAlertToggles = () => {
  try {
    const raw = localStorage.getItem(ALERT_TOGGLES_KEY);
    if (!raw) return { d30: true, d14: true, d7: true, d1: false };
    const parsed = JSON.parse(raw);
    return {
      d30: parsed?.d30 !== false,
      d14: parsed?.d14 !== false,
      d7: parsed?.d7 !== false,
      d1: Boolean(parsed?.d1),
    };
  } catch {
    return { d30: true, d14: true, d7: true, d1: false };
  }
};

const CATEGORIES = [
  { key: 'Identity', label: 'Identity', color: '#6366f1', Icon: FiUser },
  { key: 'Compliance', label: 'Compliance', color: '#0ea5e9', Icon: FiShield },
  {
    key: 'Employment',
    label: 'Employment',
    color: '#8b5cf6',
    Icon: FiBriefcase,
  },
  {
    key: 'Certificates',
    label: 'Certificates',
    color: '#10b981',
    Icon: FiAward,
  },
  { key: 'Training', label: 'Training', color: '#f59e0b', Icon: FiBookOpen },
  { key: 'Insurance', label: 'Insurance', color: '#ef4444', Icon: FiHeart },
  {
    key: 'Police Clearance',
    label: 'Police Clearance',
    color: '#64748b',
    Icon: FiLock,
  },
  { key: 'Custom', label: 'Custom', color: '#a855f7', Icon: FiLayers },
];

const resolveEmployeeName = (user) => {
  const first = String(user?.firstName || '').trim();
  const last = String(user?.lastName || '').trim();
  return [first, last].filter(Boolean).join(' ') || '—';
};

const buildEmployeeCodeMap = (users = []) => {
  const prefixCount = { ADM: 0, MGM: 0, SUP: 0, CLR: 0, CTR: 0, EMP: 0 };

  return users.reduce((acc, user) => {
    const userId = String(user?.id || '');
    if (!userId) return acc;

    const realCode = String(user.employeeCode || '').trim().toUpperCase();
    if (realCode) {
      acc[userId] = realCode;
      return acc;
    }

    const label = workforceRoleLabel(user.roles, user.role);
    let prefix = 'EMP';
    if (label === 'Admin') prefix = 'ADM';
    else if (label === 'Manager') prefix = 'MGM';
    else if (label === 'Supervisor') prefix = 'SUP';
    else if (label === 'Cleaner') prefix = 'CLR';
    else if (label === 'Contractor') prefix = 'CTR';

    prefixCount[prefix] += 1;
    acc[userId] = `${prefix}${String(prefixCount[prefix]).padStart(3, '0')}`;
    return acc;
  }, {});
};

const resolveUploadUrl = (key) => {
  const value = String(key || '').trim();
  if (!value) return '';
  if (/^(https?:)?\/\//i.test(value) || value.startsWith('data:')) return value;
  const bucket = process.env.REACT_APP_AWS_BUCKET_NAME;
  const region = process.env.REACT_APP_AWS_REGION;
  if (bucket && region)
    return `https://${bucket}.s3.${region}.amazonaws.com/${value}`;
  return '';
};

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const daysUntil = (dateValue) => {
  if (!dateValue) return null;
  const d = new Date(dateValue);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - today.getTime()) / 86400000);
};

const expiryBucketFromDays = (days) => {
  if (days === null || days === undefined) return 'none';
  if (days < 0) return 'expired';
  if (days <= 30) return 'expiring';
  return 'valid';
};

const getPreviewKind = (fileName = '', fileUrl = '', fileType = '') => {
  const name = String(fileName || '').toLowerCase();
  const url = String(fileUrl || '').toLowerCase();
  const type = String(fileType || '').toLowerCase();
  const source = `${name} ${url.split('?')[0]} ${type}`;
  if (
    type.startsWith('image/') ||
    source.startsWith('data:image/') ||
    /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(source)
  ) {
    return 'image';
  }
  if (
    type.includes('pdf') ||
    source.startsWith('data:application/pdf') ||
    /\.pdf$/i.test(source)
  ) {
    return 'pdf';
  }
  if (
    type.includes('word') ||
    type.includes('msword') ||
    type.includes('officedocument') ||
    /\.(docx?|rtf)$/i.test(source)
  ) {
    return 'doc';
  }
  return 'other';
};

export default function Document() {
  const { get, patch } = useAdminPageSearch();
  const filterUserId = get('userId');
  const filterEmployeeCode = get('employeeCode');
  const activeTab =
    get('tab') === 'compliance' ? 'compliance' : 'documents';
  const showAddDoc = get('action') === 'add';
  const editId = get('edit');
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [bulkUploadMode, setBulkUploadMode] = useState('employee');
  const [documents, setDocuments] = useState([]);
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [employeeCodeMap, setEmployeeCodeMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [employeeFilter, setEmployeeFilter] = useState('all');
  const [branchFilter, setBranchFilter] = useState('all');
  const [expiryFilter, setExpiryFilter] = useState('all');
  const [dragOver, setDragOver] = useState(false);
  const [sendingExpiryEmail, setSendingExpiryEmail] = useState(false);
  const [alertToggles, setAlertToggles] = useState(loadAlertToggles);
  const [previewDoc, setPreviewDoc] = useState(null);

  const fetchDocuments = useCallback(async (showLoader = true) => {
    try {
      if (showLoader) setLoading(true);
      // Always load every company document — employee filter is client-side only.
      const docsData = await graphqlRequest(GET_WORKFORCE_DOCUMENTS);
      setDocuments(
        Array.isArray(docsData?.getWorkforceDocuments)
          ? docsData.getWorkforceDocuments
          : [],
      );

      try {
        const allUsers = await fetchAllCompanyUsers(
          graphqlRequest,
          GET_ALL_USERS,
        );
        setUsers(allUsers);
        setEmployeeCodeMap(buildEmployeeCodeMap(allUsers));
      } catch (userErr) {
        console.error('Error fetching users for employee codes:', userErr);
        setUsers([]);
        setEmployeeCodeMap({});
      }

      try {
        const branchesData = await graphqlRequest(GET_BRANCHES);
        setBranches(
          Array.isArray(branchesData?.getMyCompanyBranches)
            ? branchesData.getMyCompanyBranches
            : [],
        );
      } catch (branchErr) {
        console.error('Error fetching branches:', branchErr);
        setBranches([]);
      }
    } catch (err) {
      console.error('Error fetching documents:', err);
      toast.error(err?.message || 'Failed to fetch documents');
      setDocuments([]);
    } finally {
      if (showLoader) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDocuments(true);
  }, [fetchDocuments]);

  // Stay in sync when employees upload/edit from the employee portal
  useEffect(() => {
    const refreshQuietly = () => {
      void fetchDocuments(false);
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refreshQuietly();
    };
    window.addEventListener('focus', refreshQuietly);
    window.addEventListener('workforce-documents-refresh', refreshQuietly);
    window.addEventListener('admin-backend-notifications-refresh', refreshQuietly);
    document.addEventListener('visibilitychange', onVisibility);
    const intervalId = window.setInterval(refreshQuietly, 25000);
    return () => {
      window.removeEventListener('focus', refreshQuietly);
      window.removeEventListener('workforce-documents-refresh', refreshQuietly);
      window.removeEventListener(
        'admin-backend-notifications-refresh',
        refreshQuietly,
      );
      document.removeEventListener('visibilitychange', onVisibility);
      window.clearInterval(intervalId);
    };
  }, [fetchDocuments]);

  useEffect(() => {
    if (!filterUserId && !filterEmployeeCode) return;
    if (filterUserId) {
      const user = users.find((u) => String(u.id) === String(filterUserId));
      if (user) {
        setEmployeeFilter(resolveEmployeeName(user));
        return;
      }
    }
    if (filterEmployeeCode) {
      const match = documents.find(
        (d) =>
          String(d.employeeCode || '').toUpperCase() ===
          String(filterEmployeeCode).toUpperCase(),
      );
      if (match?.employeeName) setEmployeeFilter(match.employeeName);
    }
  }, [filterUserId, filterEmployeeCode, users, documents]);

  const clearEmployeeUrlFilter = () => {
    setEmployeeFilter('all');
    patch({}, ['userId', 'employeeCode']);
  };

  const setActiveTab = (tab) => {
    patch({ tab: tab === 'compliance' ? 'compliance' : '' });
  };

  const groupsByKey = useMemo(() => {
    const grouped = {};
    documents.forEach((doc) => {
      const userId = String(doc.userId || '');
      const key = userId || doc.employeeCode;
      const mappedEmployeeCode = userId ? employeeCodeMap[userId] : '';
      if (!grouped[key]) {
        grouped[key] = {
          id: key,
          userId: doc.userId || '',
          employeeId: mappedEmployeeCode || doc.employeeCode,
          name: doc.employeeName,
          cleaningLicense: doc.cleaningLicense,
          experienceYears: doc.experienceYears,
          experience: `${doc.experienceYears} years`,
          personalDocs: [],
          files: [],
          recordIds: [],
        };
      }
      const fileUrl = doc.fileUrl || resolveUploadUrl(doc.fileKey);
      grouped[key].personalDocs.push({
        id: doc.id,
        name: doc.fileName,
        type: doc.fileType,
        documentType: doc.documentType,
        url: fileUrl,
      });
      grouped[key].files.push({
        id: doc.id,
        fileName: doc.fileName,
        fileUrl,
        documentType: doc.documentType,
        expiryDate: doc.expiryDate || '',
        siteReference: doc.siteReference || '',
      });
      grouped[key].recordIds.push(doc.id);
      grouped[key].cleaningLicense = doc.cleaningLicense;
      grouped[key].experienceYears = doc.experienceYears;
      grouped[key].experience = `${doc.experienceYears} years`;
    });
    return grouped;
  }, [documents, employeeCodeMap]);

  const editGroup = useMemo(
    () => (editId ? groupsByKey[editId] || null : null),
    [editId, groupsByKey],
  );

  const cardItems = useMemo(() => {
    const branchById = new Map(
      (branches || []).map((b) => [b.id, b.name || b.location || '—']),
    );
    const usersById = new Map((users || []).map((u) => [String(u.id), u]));

    return documents.map((doc) => {
      const userId = String(doc.userId || '');
      const key = userId || doc.employeeCode;
      const category = categorizeDocumentType(doc.documentType);
      const fileUrl = doc.fileUrl || resolveUploadUrl(doc.fileKey);
      const rawStatus = String(doc.verificationStatus || '').trim();
      // Uploaded ≠ verified. Only "Verified" from the API counts; everything else is Pending.
      const status = rawStatus === 'Verified' ? 'Verified' : 'Pending';
      const owner = usersById.get(userId) || null;
      const branchId = owner?.branchId || '';
      const branchName = branchId ? branchById.get(branchId) || '—' : '—';
      const expiryDate = doc.expiryDate ? new Date(doc.expiryDate) : null;
      const days = daysUntil(expiryDate);
      const expiryBucket = expiryBucketFromDays(days);
      return {
        id: doc.id,
        title: doc.documentType || doc.fileName || 'Document',
        fileName: doc.fileName,
        employeeName: doc.employeeName || '—',
        employeeCode: employeeCodeMap[userId] || doc.employeeCode || '',
        userId,
        branchId,
        branchName,
        category,
        status,
        uploaded: formatDate(doc.createdAt),
        expiry: formatDate(expiryDate),
        expiryBucket,
        verifiedBy: status === 'Verified' ? doc.verifiedBy || 'Admin' : '—',
        fileUrl,
        fileType: doc.fileType || '',
        groupKey: key,
      };
    });
  }, [documents, employeeCodeMap, users, branches]);

  const employeeOptions = useMemo(() => {
    const map = new Map();
    (users || []).filter(isWorkforcePerson).forEach((u) => {
      const name = `${u.firstName || ''} ${u.lastName || ''}`.trim();
      if (name) map.set(name, name);
    });
    cardItems.forEach((c) => {
      const label = c.employeeName || c.employeeCode;
      if (label && label !== '—') map.set(label, label);
    });
    return Array.from(map.values()).sort((a, b) => a.localeCompare(b));
  }, [cardItems, users]);

  const branchOptions = useMemo(() => {
    return [...branches]
      .map((b) => ({
        id: b.id,
        label: b.name || b.location || 'Branch',
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [branches]);

  const filteredCards = useMemo(() => {
    const q = search.trim().toLowerCase();
    const urlUserId = String(filterUserId || '').trim();
    const urlCode = String(filterEmployeeCode || '').trim().toUpperCase();
    return cardItems.filter((c) => {
      if (categoryFilter !== 'all' && c.category !== categoryFilter) return false;
      if (statusFilter !== 'all' && c.status !== statusFilter) return false;
      if (typeFilter !== 'all' && c.title !== typeFilter) return false;
      if (branchFilter !== 'all') {
        if (branchFilter === 'unassigned') {
          if (c.branchId) return false;
        } else if (c.branchId !== branchFilter) {
          return false;
        }
      }
      if (expiryFilter !== 'all' && c.expiryBucket !== expiryFilter) return false;
      if (employeeFilter !== 'all') {
        const byName = c.employeeName === employeeFilter;
        const byId = urlUserId && c.userId === urlUserId;
        const byCode =
          urlCode &&
          String(c.employeeCode || '').toUpperCase() === urlCode;
        if (!byName && !byId && !byCode) return false;
      }
      if (!q) return true;
      return (
        c.title.toLowerCase().includes(q) ||
        c.employeeName.toLowerCase().includes(q) ||
        c.fileName.toLowerCase().includes(q) ||
        c.employeeCode.toLowerCase().includes(q) ||
        c.category.toLowerCase().includes(q) ||
        String(c.branchName || '')
          .toLowerCase()
          .includes(q)
      );
    });
  }, [
    cardItems,
    search,
    categoryFilter,
    statusFilter,
    typeFilter,
    employeeFilter,
    branchFilter,
    expiryFilter,
    filterUserId,
    filterEmployeeCode,
  ]);

  const categoryCounts = useMemo(() => {
    const counts = Object.fromEntries(CATEGORIES.map((c) => [c.key, 0]));
    cardItems.forEach((c) => {
      if (counts[c.category] != null) counts[c.category] += 1;
      else counts.Custom += 1;
    });
    return counts;
  }, [cardItems]);

  const categoryExpected = useMemo(() => {
    const map = {};
    CATEGORIES.forEach((c) => {
      map[c.key] = (DOCUMENT_CATEGORY_TYPES[c.key] || []).length || 1;
    });
    return map;
  }, []);

  const stats = useMemo(() => {
    const workforceUsers = users.filter(isWorkforcePerson);
    const employeeIds = new Set(workforceUsers.map((u) => String(u.id)));
    const withDocs = new Set(
      documents.map((d) => String(d.userId || '')).filter(Boolean),
    );
    const totalEmployees = Math.max(
      employeeIds.size,
      withDocs.size,
      1,
    );
    const covered = employeeIds.size
      ? [...withDocs].filter((id) => employeeIds.has(id)).length
      : withDocs.size;
    const completion = Math.round((covered / totalEmployees) * 100);
    const missing = Math.max(0, totalEmployees - withDocs.size);
    return {
      completion: Math.min(100, documents.length ? completion || 100 : 0),
      missing,
      totalDocs: documents.length,
      peopleWithDocs: withDocs.size,
    };
  }, [users, documents]);

  const ocrSample = filteredCards[0] || cardItems[0] || null;

  const handleDocumentAdded = async () => {
    await fetchDocuments(false);
    window.dispatchEvent(new Event('workforce-documents-refresh'));
  };

  const openEditForCard = (card) => {
    if (card?.groupKey) patch({ edit: card.groupKey }, ['action']);
  };

  const handleDeleteCard = async (card) => {
    if (
      !await appConfirmMessage(
        `Delete "${card.title}" for ${card.employeeName}? This cannot be undone.`,
      )
    ) {
      return;
    }
    try {
      await graphqlRequest(REMOVE_WORKFORCE_DOCUMENT, { id: card.id });
      toast.success('Document deleted successfully.');
      if (editGroup?.recordIds?.includes(card.id)) {
        patch({}, ['edit']);
      }
      await fetchDocuments(false);
      window.dispatchEvent(new Event('workforce-documents-refresh'));
    } catch (err) {
      console.error('Error deleting document:', err);
      toast.error(err?.message || 'Failed to delete document');
    }
  };

  const resolveVerifierLabel = () => {
    try {
      const raw =
        Cookies.get('user') ||
        Cookies.get('adminUser') ||
        localStorage.getItem('user') ||
        '';
      if (raw) {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        const name = [parsed?.firstName, parsed?.lastName]
          .filter(Boolean)
          .join(' ')
          .trim();
        if (name) return name;
        if (parsed?.email) return String(parsed.email);
      }
    } catch {
      /* ignore */
    }
    return 'Admin';
  };

  const handleVerifyCard = async (card) => {
    if (card.status === 'Verified') return;
    try {
      await graphqlRequest(UPDATE_DOC_VERIFICATION, {
        input: {
          id: card.id,
          verificationStatus: 'Verified',
          verifiedBy: resolveVerifierLabel(),
        },
      });
      toast.success('Document marked as verified.');
      await fetchDocuments(false);
      window.dispatchEvent(new Event('workforce-documents-refresh'));
    } catch (err) {
      console.error('Error verifying document:', err);
      toast.error(err?.message || 'Failed to verify document');
    }
  };

  const updateAlertToggle = (key) => {
    setAlertToggles((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try {
        localStorage.setItem(ALERT_TOGGLES_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const enabledThresholds = () => {
    const list = [];
    if (alertToggles.d30) list.push(30);
    if (alertToggles.d14) list.push(14);
    if (alertToggles.d7) list.push(7);
    if (alertToggles.d1) list.push(1);
    return list.length ? list : [30];
  };

  const handleSendExpiryEmails = async () => {
    if (sendingExpiryEmail) return;
    setSendingExpiryEmail(true);
    try {
      const data = await graphqlRequest(SEND_EXPIRY_EMAILS, {
        thresholds: enabledThresholds(),
      });
      const result = data?.sendWorkforceDocumentExpiryEmails;
      const sent = Number(result?.sent || 0);
      const message = String(result?.message || '').trim();
      if (sent > 0) {
        toast.success(
          message ||
            `Expiry alerts sent (${sent} email/in-app notification(s) to employees).`,
        );
      } else {
        toast.info(
          message ||
            'No expiry alerts sent — no matching documents, or recipients missing email/user link.',
        );
      }
    } catch (err) {
      console.error('Expiry alert failed:', err);
      toast.error(err?.message || 'Failed to send expiry alerts');
    } finally {
      setSendingExpiryEmail(false);
    }
  };

  const openUpload = () => patch({ action: 'add' }, ['edit']);

  const openDocumentPreview = useCallback((card) => {
    if (!card?.fileUrl) return;
    const kind = getPreviewKind(card.fileName, card.fileUrl, card.fileType);
    // PDFs / Word docs: open in a new tab (iframe often forces download).
    if (kind === 'pdf' || kind === 'doc' || kind === 'other') {
      window.open(card.fileUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    setPreviewDoc(card);
  }, []);

  const openBulkUpload = (mode = 'employee') => {
    setBulkUploadMode(mode === 'documentType' ? 'documentType' : 'employee');
    setShowBulkUpload(true);
  };

  const onDropZoneClick = () => openBulkUpload('employee');

  const onDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const onDragLeave = () => setDragOver(false);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    openBulkUpload('employee');
    toast.info('Choose By employee or By document type, then assign files.');
  };

  const categoryMeta = (key) =>
    CATEGORIES.find((c) => c.key === key) || CATEGORIES[CATEGORIES.length - 1];

  const filterLabel =
    employeeFilter !== 'all'
      ? employeeFilter
      : filterEmployeeCode ||
        (filterUserId
          ? employeeCodeMap[filterUserId] || 'Selected employee'
          : '');

  return (
    <div className="dw-page">
      <div className="dw-header">
        <div>
          <h1>Documents</h1>
          <p>
            Manage workforce documents, licences and compliance in one place
            {filterLabel ? ` · filtered: ${filterLabel}` : ''}.
          </p>
        </div>
        {activeTab === 'documents' ? (
          <div className="dw-header-actions">
            <button
              type="button"
              className="dw-btn-outline"
              onClick={() => openBulkUpload('employee')}
            >
              <FiUploadCloud /> Bulk Upload
            </button>
            <button type="button" className="dw-btn-primary" onClick={openUpload}>
              <FiPlus /> Upload Documents
            </button>
          </div>
        ) : null}
      </div>

      <div className="dw-tabs" role="tablist" aria-label="Documents sections">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'documents'}
          className={`dw-tab${activeTab === 'documents' ? ' active' : ''}`}
          onClick={() => setActiveTab('documents')}
        >
          <FiFileText /> Documents
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'compliance'}
          className={`dw-tab${activeTab === 'compliance' ? ' active' : ''}`}
          onClick={() => setActiveTab('compliance')}
        >
          <FiShield /> Licences & Compliance
        </button>
      </div>

      {activeTab === 'compliance' ? (
        <LicencesCompliance embedded />
      ) : (
        <>
      {(employeeFilter !== 'all' || filterUserId || filterEmployeeCode) && (
        <div className="dw-filter-banner">
          <span>
            Showing documents for <strong>{filterLabel || 'selected employee'}</strong>.
            All company documents remain available.
          </span>
          <button type="button" className="dw-link-btn" onClick={clearEmployeeUrlFilter}>
            View all documents
          </button>
        </div>
      )}

      <div className="dw-filters">
        <div className="dw-search">
          <FiSearch aria-hidden />
          <input
            type="search"
            placeholder="Search employee, passport, visa, licence..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          aria-label="Document type"
        >
          <option value="all">Document type</option>
          {DOCUMENT_TYPE_OPTIONS.filter((t) => t !== 'Other').map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          aria-label="Category"
        >
          <option value="all">Category</option>
          {CATEGORIES.map((c) => (
            <option key={c.key} value={c.key}>
              {c.label}
            </option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          aria-label="Verification"
        >
          <option value="all">Verification</option>
          <option value="Verified">Verified</option>
          <option value="Pending">Pending</option>
        </select>
        <select
          value={employeeFilter}
          onChange={(e) => {
            setEmployeeFilter(e.target.value);
            if (e.target.value === 'all') clearEmployeeUrlFilter();
          }}
          aria-label="Employee"
        >
          <option value="all">All employees</option>
          {employeeOptions.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
        <select
          value={branchFilter}
          onChange={(e) => setBranchFilter(e.target.value)}
          aria-label="Branch"
        >
          <option value="all">Branch</option>
          <option value="unassigned">Unassigned</option>
          {branchOptions.map((b) => (
            <option key={b.id} value={b.id}>
              {b.label}
            </option>
          ))}
        </select>
        <select
          value={expiryFilter}
          onChange={(e) => setExpiryFilter(e.target.value)}
          aria-label="Expiry"
        >
          <option value="all">Expiry</option>
          <option value="valid">Valid (&gt;30d)</option>
          <option value="expiring">Expiring (≤30d)</option>
          <option value="expired">Expired</option>
          <option value="none">No expiry date</option>
        </select>
      </div>

      <div className="dw-stats">
        <div className="dw-stat-card">
          <div className="dw-ring" style={{ '--pct': stats.completion }}>
            <div className="dw-ring-inner">{stats.completion}%</div>
          </div>
          <div className="dw-stat-body">
            <h3>Documents</h3>
            <p>
              Completion across workforce
              <br />
              <span className="dw-stat-trend">{stats.totalDocs} on file · {stats.peopleWithDocs} people</span>
            </p>
          </div>
        </div>
        <div className="dw-stat-card dw-stat-card--danger">
          <div className="dw-stat-icon dw-stat-icon--danger">
            <FiAlertTriangle />
          </div>
          <div className="dw-stat-body">
            <h3>Missing</h3>
            <p>
              {stats.missing} employee
              {stats.missing === 1 ? '' : 's'} without documents
            </p>
          </div>
        </div>
        <div className="dw-stat-card dw-stat-card--warn">
          <div className="dw-stat-icon dw-stat-icon--warn">
            <FiCalendar />
          </div>
          <div className="dw-stat-body">
            <h3>Upcoming Visa Expiry</h3>
            <p>Track visa dates when captured on upload</p>
          </div>
        </div>
        <div className="dw-stat-card dw-stat-card--alert">
          <div className="dw-stat-icon dw-stat-icon--alert">
            <FiShield />
          </div>
          <div className="dw-stat-body">
            <h3>Upcoming WWCC Expiry</h3>
            <p>Track WWCC dates when captured on upload</p>
          </div>
        </div>
      </div>

      <section className="dw-categories">
        <div className="dw-section-head">
          <h2>Document Categories</h2>
          <button
            type="button"
            className="dw-link-btn"
            onClick={() => setCategoryFilter('all')}
          >
            View All
          </button>
        </div>
        <div className="dw-cat-row">
          {CATEGORIES.map(({ key, label, color, Icon }) => (
            <button
              type="button"
              key={key}
              className={`dw-cat-card${
                categoryFilter === key ? ' active' : ''
              }`}
              onClick={() =>
                setCategoryFilter((prev) => (prev === key ? 'all' : key))
              }
            >
              <div className="dw-cat-icon" style={{ background: color }}>
                <Icon />
              </div>
              <span>{label}</span>
              <small>
                {categoryCounts[key] || 0}/{categoryExpected[key] || 8}
              </small>
            </button>
          ))}
        </div>
      </section>

      <section className="dw-docs-section">
        <div className="dw-section-head">
          <h2>Documents {filteredCards.length}</h2>
        </div>
        <div className="dw-docs-grid">
          {loading ? (
            <div className="dw-loading">Loading documents…</div>
          ) : filteredCards.length === 0 ? (
            <div className="dw-empty">
              No documents found. Click “Upload Documents” to add one.
            </div>
          ) : (
            filteredCards.map((card) => {
              const meta = categoryMeta(card.category);
              const Icon = meta.Icon || FiFileText;
              const badgeClass =
                card.status === 'Verified'
                  ? 'dw-badge--verified'
                  : card.status === 'Pending'
                    ? 'dw-badge--pending'
                    : 'dw-badge--missing';
              return (
                <article
                  key={card.id}
                  className={`dw-doc-card${card.fileUrl ? ' is-clickable' : ''}`}
                  onClick={() => {
                    if (card.fileUrl) openDocumentPreview(card);
                  }}
                  onKeyDown={(e) => {
                    if (!card.fileUrl) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openDocumentPreview(card);
                    }
                  }}
                  role={card.fileUrl ? 'button' : undefined}
                  tabIndex={card.fileUrl ? 0 : undefined}
                >
                  <div className="dw-doc-card-top">
                    <div
                      className="dw-doc-type-icon"
                      style={{ background: meta.color }}
                    >
                      <Icon />
                    </div>
                    <div className="dw-doc-title">
                      <h3 title={card.title}>{card.title}</h3>
                      <p>{card.employeeName}</p>
                    </div>
                    <span className={`dw-badge ${badgeClass}`}>
                      {card.status}
                    </span>
                  </div>
                  <dl className="dw-doc-meta">
                    <div>
                      <dt>Uploaded</dt>
                      <dd>{card.uploaded}</dd>
                    </div>
                    <div>
                      <dt>Expiry</dt>
                      <dd>{card.expiry}</dd>
                    </div>
                    <div>
                      <dt>Verified by</dt>
                      <dd>{card.verifiedBy}</dd>
                    </div>
                    <div>
                      <dt>File</dt>
                      <dd title={card.fileName}>
                        {card.fileName?.slice(0, 18) || '—'}
                        {card.fileName?.length > 18 ? '…' : ''}
                      </dd>
                    </div>
                  </dl>
                  <div
                    className="dw-doc-actions"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {card.fileUrl ? (
                      <button
                        type="button"
                        className="dw-icon-btn dw-icon-btn--link"
                        title="Preview"
                        aria-label="Preview"
                        onClick={() => openDocumentPreview(card)}
                      >
                        <FiEye />
                      </button>
                    ) : null}
                    {card.status !== 'Verified' ? (
                      <button
                        type="button"
                        className="dw-icon-btn dw-icon-btn--verify"
                        title="Mark as verified"
                        aria-label="Mark as verified"
                        onClick={() => void handleVerifyCard(card)}
                      >
                        <FiCheckCircle />
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="dw-icon-btn"
                      title="Edit"
                      aria-label="Edit"
                      onClick={() => openEditForCard(card)}
                    >
                      <FiEdit2 />
                    </button>
                    <button
                      type="button"
                      className="dw-icon-btn dw-icon-btn--danger"
                      title="Delete"
                      aria-label="Delete"
                      onClick={() => void handleDeleteCard(card)}
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                </article>
              );
            })
          )}
        </div>
      </section>

      <div className="dw-bottom">
        <section className="dw-panel">
          <h2>Bulk Upload</h2>
          <div
            className={`dw-dropzone${dragOver ? ' drag-over' : ''}`}
            onClick={onDropZoneClick}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') openBulkUpload('employee');
            }}
          >
            <FiUploadCloud />
            <strong>Open bulk upload</strong>
            <small>By employee or by document type</small>
          </div>
          <div className="dw-bulk-actions">
            <button
              type="button"
              className="dw-bulk-action"
              onClick={() => openBulkUpload('employee')}
            >
              By employee
              <small>Many doc types for one person</small>
            </button>
            <button
              type="button"
              className="dw-bulk-action"
              onClick={() => openBulkUpload('documentType')}
            >
              By document type
              <small>Same type for many people</small>
            </button>
          </div>
        </section>

        <section className="dw-panel">
          <h2>Automatic OCR Extraction</h2>
          <div className="dw-ocr-preview">
            <h3>
              {ocrSample
                ? `${ocrSample.title} — ${ocrSample.employeeName}`
                : 'No document selected'}
            </h3>
            <dl className="dw-ocr-grid">
              <div>
                <dt>Full Name</dt>
                <dd>{ocrSample?.employeeName || '—'}</dd>
              </div>
              <div>
                <dt>Document Type</dt>
                <dd>{ocrSample?.title || '—'}</dd>
              </div>
              <div>
                <dt>Uploaded</dt>
                <dd>{ocrSample?.uploaded || '—'}</dd>
              </div>
              <div>
                <dt>Category</dt>
                <dd>{ocrSample?.category || '—'}</dd>
              </div>
            </dl>
            {ocrSample ? (
              <div className="dw-ocr-status">
                <FiCheckCircle /> Extraction complete — review and confirm.
              </div>
            ) : null}
          </div>
        </section>

        <section className="dw-panel">
          <h2>Expiry Alerts</h2>
          <p className="dw-alert-note">
            Alerts go to <strong>employees</strong> whose documents or visas are
            due — by <strong>email</strong> and <strong>in-app notification</strong>{' '}
            (not to admins). Send on demand below; a daily run also goes out at
            8:00 AM.
          </p>
          <div className="dw-alert-list">
            {[
              { key: 'd30', label: '30 days before expiry', cls: 'd30' },
              { key: 'd14', label: '14 days before expiry', cls: 'd14' },
              { key: 'd7', label: '7 days before expiry', cls: 'd7' },
              { key: 'd1', label: '1 day before expiry', cls: 'd1' },
            ].map((row) => (
              <div key={row.key} className="dw-alert-row">
                <span>{row.label}</span>
                <button
                  type="button"
                  className={`dw-alert-toggle ${row.cls} ${
                    alertToggles[row.key] ? 'on' : 'off'
                  }`}
                  onClick={() => updateAlertToggle(row.key)}
                >
                  {alertToggles[row.key] ? 'On' : 'Off'}
                </button>
              </div>
            ))}
          </div>
          <div className="dw-alert-channels">
            <button
              type="button"
              className="dw-alert-channel active"
              disabled={sendingExpiryEmail}
              onClick={() => void handleSendExpiryEmails()}
            >
              {sendingExpiryEmail ? 'Sending…' : 'Email digest now'}
            </button>
          </div>
        </section>
      </div>
        </>
      )}

      {showAddDoc && (
        <AddDocument
          onClose={() => patch({}, ['action'])}
          onDocumentAdded={handleDocumentAdded}
          initialUserId={filterUserId}
          initialEmployeeCode={filterEmployeeCode}
        />
      )}

      {showBulkUpload ? (
        <BulkUpload
          initialMode={bulkUploadMode}
          onClose={() => setShowBulkUpload(false)}
          onDone={handleDocumentAdded}
        />
      ) : null}

      {editGroup && (
        <EditDocument
          group={editGroup}
          onClose={() => patch({}, ['edit'])}
          onSaved={async () => {
            await fetchDocuments(false);
            window.dispatchEvent(new Event('workforce-documents-refresh'));
          }}
        />
      )}

      {previewDoc ? (
        <div
          className="dw-preview-overlay"
          onClick={() => setPreviewDoc(null)}
          role="presentation"
        >
          <div
            className="dw-preview-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Document preview"
          >
            <div className="dw-preview-header">
              <div>
                <h3>{previewDoc.title || 'Document'}</h3>
                <p>
                  {previewDoc.employeeName}
                  {previewDoc.fileName ? ` · ${previewDoc.fileName}` : ''}
                </p>
              </div>
              <div className="dw-preview-header-actions">
                <a
                  className="dw-preview-open-tab"
                  href={previewDoc.fileUrl}
                  target="_blank"
                  rel="noreferrer"
                  title="Open in new tab"
                >
                  <FiExternalLink /> Open tab
                </a>
                <button
                  type="button"
                  className="dw-preview-close"
                  onClick={() => setPreviewDoc(null)}
                >
                  ×
                </button>
              </div>
            </div>
            <div className="dw-preview-body">
              {getPreviewKind(
                previewDoc.fileName,
                previewDoc.fileUrl,
                previewDoc.fileType,
              ) === 'image' ? (
                <img
                  src={previewDoc.fileUrl}
                  alt={previewDoc.fileName || previewDoc.title || 'Document'}
                  className="dw-preview-image"
                />
              ) : (
                <div className="dw-preview-fallback">
                  <FiFileText />
                  <p>This file opens in a new browser tab.</p>
                  <a
                    href={previewDoc.fileUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open in a new tab
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
